import { DateTime } from 'luxon';
import { injectStrict } from '@/injection/injectStrict.ts';
import { AuthKey } from '@/injection/keys.ts';
import { Car, CarAdmissionType, CarStatus } from '@/types/car.type.ts';
import { Auction, AuctionStatus } from '@/types/auction.type.ts';
import { User, UserStatus } from '@/types/user.type.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function usePermission() {
    const $Auth = injectStrict(AuthKey);

    function isAdmin(): boolean {
        // return true;

        return $Auth.getCurrentUser().is_admin;
    }

    function isCertifiedDealer(): boolean {
        return $Auth.getCurrentUser().is_certified_dealer;
    }

    function canAddCar(): boolean {
        return isCertifiedDealer();
    }

    function canEditCar(car: Car): boolean {
        if (
            isAdmin()
            && car.admission_type === CarAdmissionType.HyundaiMobility
            && [CarStatus.Draft, CarStatus.OnApproval, CarStatus.Approved].indexOf(car.status) !== -1
        ) {
            return true;
        }

        if (!isCertifiedDealer()) {
            return false;
        }

        if ([CarStatus.Draft, CarStatus.OnRevision].indexOf(car.status) < 0) {
            return false;
        }

        return car.dealer.id === $Auth.getCurrentUser().dealer_id;
    }

    function canDeleteCar(car: Car): boolean {
        if (!isCertifiedDealer()) {
            return false;
        }

        if ([CarStatus.Draft, CarStatus.OnRevision].indexOf(car.status) === -1) {
            return false;
        }

        return car.dealer.id === $Auth.getCurrentUser().dealer_id;
    }

    function canRecreateCar(car: Car): boolean {
        if (!isCertifiedDealer()) {
            return false;
        }

        if ([CarStatus.NotRealized].indexOf(car.status) === -1) {
            return false;
        }

        return car.dealer.id === $Auth.getCurrentUser().dealer_id;
    }

    function canSendCarToModeration(car: Car): boolean {
        if ([CarStatus.Draft, CarStatus.OnRevision].indexOf(car.status) === -1) {
            return false;
        }

        return isCertifiedDealer();
    }

    function canApproveCar(car: Car): boolean {
        if (car.status !== CarStatus.OnApproval) {
            return false;
        }

        return isAdmin();
    }

    function canRejectCar(car: Car): boolean {
        if (car.status !== CarStatus.OnApproval) {
            return false;
        }

        return isAdmin();
    }

    function canEditCarDocuments(car: Car): boolean {
        if (isAdmin()) {
            return true;
        }

        return canEditCar(car);
    }

    function canViewCarInvoice(car: Car): boolean {
        if (car.status !== CarStatus.Sold) {
            return false;
        }

        if (isAdmin()) {
            return true;
        }

        if (car.dealer.id === $Auth.getCurrentUser().dealer_id) {
            return true;
        }

        return car.lastAuction?.last_lot?.winner.dealer_id === $Auth.getCurrentUser().dealer_id;
    }

    function canViewCarSaleContract(car: Car): boolean {
        if (car.status !== CarStatus.Sold) {
            return false;
        }

        if (isAdmin()) {
            return true;
        }

        if (car.dealer.id === $Auth.getCurrentUser().dealer_id) {
            return true;
        }

        return car.lastAuction?.last_lot?.winner.dealer_id === $Auth.getCurrentUser().dealer_id;
    }

    function canCreateAuction(): boolean {
        return isAdmin();
    }

    function canSendToModerationCar(car: Car): boolean {
        if (!isCertifiedDealer()) {
            return false;
        }

        if ([CarStatus.Draft, CarStatus.OnRevision].indexOf(car.status) === -1) {
            return false;
        }

        return car.dealer.id === $Auth.getCurrentUser().dealer_id;
    }

    function canCreateBid(auction: Auction): boolean {
        if (isAdmin()) {
            return false;
        }

        if (auction.status !== AuctionStatus.InProgress) {
            return false;
        }

        return !auction.is_user_lot;
    }

    function canForceCompleteAuction(auction: Auction): boolean {
        if (!isAdmin()) {
            return false;
        }

        return auction.status === AuctionStatus.InProgress;
    }

    function canProlongAuction(auction: Auction): boolean {
        if (!isAdmin()) {
            return false;
        }

        if (auction.status === AuctionStatus.CompletedWithoutBids) {
            const localizedEndDate = DateTime.fromSQL(auction.end_date, { zone: 'UTC' }).toLocal();
            const millisLater = DateTime.now().diff(localizedEndDate).toMillis();

            return millisLater < 1000 * 60 * 60;
        }

        return auction.status === AuctionStatus.InProgress;
    }

    function canEditAuction(auction: Auction): boolean {
        if (!isAdmin()) {
            return false;
        }

        // noinspection RedundantIfStatementJS
        if ([AuctionStatus.Draft, AuctionStatus.AwaitingStart].indexOf(auction.status) < 0) {
            return false;
        }

        return true;
    }

    function canSendAnnouncementAboutAuction(auction: Auction): boolean {
        if (!isAdmin()) {
            return false;
        }

        return auction.status === AuctionStatus.AwaitingStart;
    }

    function canAnnulAuction(auction: Auction): boolean {
        if (!isAdmin()) {
            return false;
        }

        return auction.status === AuctionStatus.Completed;
    }

    function canViewAuctionBids(): boolean {
        // noinspection RedundantIfStatementJS
        if (isAdmin()) {
            return true;
        }

        return false;
    }

    function canConfirmAuctionWinner(auction: Auction): boolean {
        if (auction.status !== AuctionStatus.AwaitingResult) {
            return false;
        }

        return auction.car.creator?.id === $Auth.getCurrentUser().id;
    }

    function canViewAuctionInvoice(auction: Auction): boolean {
        if (auction.car.status !== CarStatus.Sold) {
            return false;
        }

        if (isAdmin()) {
            return true;
        }

        if (auction.car.dealer.id === $Auth.getCurrentUser().dealer_id) {
            return true;
        }

        return auction.is_winner || auction.winner?.dealer_id === $Auth.getCurrentUser().dealer_id;
    }

    function canViewAuctionSaleContract(auction: Auction): boolean {
        if (auction.car.status !== CarStatus.Sold) {
            return false;
        }

        if (isAdmin()) {
            return true;
        }

        if (auction.car.dealer.id === $Auth.getCurrentUser().dealer_id) {
            return true;
        }

        return auction.is_winner || auction.winner?.dealer_id === $Auth.getCurrentUser().dealer_id;
    }

    function canSetDeliveryRequired(auction: Auction): boolean {
        if (auction.status !== AuctionStatus.Completed) {
            return false;
        }

        return auction.is_winner || auction.winner?.user_id === $Auth.getCurrentUser().id;
    }

    function canCreateDocument(): boolean {
        return isAdmin();
    }

    function canDeleteDocument(): boolean {
        return isAdmin();
    }

    function canApproveUser(user: User): boolean {
        return [UserStatus.Moderation, UserStatus.ModerationRejected].includes(user.status);
    }

    function canRejectUser(user: User): boolean {
        return user.status === UserStatus.Moderation;
    }

    function canChangeActivityStatusUser(user: User): boolean {
        return [UserStatus.Active, UserStatus.Deactivated].includes(user.status);
    }

    return {
        isAdmin: isAdmin,
        isCertifiedDealer: isCertifiedDealer,
        canAddCar: canAddCar,
        canEditCar: canEditCar,
        canDeleteCar: canDeleteCar,
        canRecreateCar: canRecreateCar,
        canSendCarToModeration: canSendCarToModeration,
        canApproveCar: canApproveCar,
        canRejectCar: canRejectCar,
        canEditCarDocuments: canEditCarDocuments,
        canViewCarInvoice: canViewCarInvoice,
        canViewCarSaleContract: canViewCarSaleContract,
        canSendToModerationCar: canSendToModerationCar,
        canCreateAuction: canCreateAuction,
        canCreateBid: canCreateBid,
        canForceCompleteAuction: canForceCompleteAuction,
        canProlongAuction: canProlongAuction,
        canEditAuction: canEditAuction,
        canSendAnnouncementAboutAuction: canSendAnnouncementAboutAuction,
        canAnnulAuction: canAnnulAuction,
        canViewAuctionBids: canViewAuctionBids,
        canConfirmAuctionWinner: canConfirmAuctionWinner,
        canViewAuctionInvoice: canViewAuctionInvoice,
        canViewAuctionSaleContract: canViewAuctionSaleContract,
        canSetDeliveryRequired: canSetDeliveryRequired,
        canCreateDocument: canCreateDocument,
        canDeleteDocument: canDeleteDocument,
        canApproveUser: canApproveUser,
        canRejectUser: canRejectUser,
        canChangeActivityStatusUser: canChangeActivityStatusUser,
    };
}
